import { Component, Input, ViewChild } from '@angular/core';
import { SelectItemInterface } from '../../components/select/select.component';
import { Document, Enterprise, Insured, User } from '../../models/models';
import { ModalController } from '@ionic/angular';
import { DocumentService } from '../../services/document.service';
import { Modal } from '../modal';
import { PopulationService } from '../../services/population.service';
import { DataService } from '../../services/data.service';
import { SearchService } from '../../services/search.service';
import { ToastService } from '../../services/toast.service';
import { ShareService } from '../../services/share.service';
import { AuthService } from '../../services/auth.service';
import { AppComponent } from '../../app.component';
import { FormControl, Validators } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { PickedFile } from '@capawesome/capacitor-file-picker';
import { LoaderUtils } from 'src/app/utils/loader.utils';
import * as moment from 'moment';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-share-create-modal',
  templateUrl: './share-create-modal.component.html',
  styleUrls: ['./share-create-modal.component.scss'],
})
export class ShareCreateModalComponent extends Modal {
  @Input() insured: Insured;
  @Input() documentsSelection: Document[] = [];
  @Input() enterprisesSelection: Enterprise[] = [];
  @Input() selectedTarget: string;
  @Input() forceTarget = false;
  @Input() canUploadDocuments = true;
  insureds: SelectItemInterface[];
  documents: SelectItemInterface[];
  enterprises: SelectItemInterface[];
  targets: SelectItemInterface[];
  user: User;
  form: FormGroup = new FormGroup({
    target: new FormControl('', [Validators.required]),
    documents: new FormControl([]),
    message: new FormControl(''),
    insureds: new FormControl([]),
    enterprises: new FormControl([]),
    files: new FormControl([]),
    allEnterprises: new FormControl(false),
    allInsureds: new FormControl(false),
  });
  destinationTitle = '';
  sendButtonLabel = 'Envoyer';

  constructor(
    modalCtrl: ModalController,
    private documentService: DocumentService,
    private populationService: PopulationService,
    private dataService: DataService,
    private toastService: ToastService,
    private shareService: ShareService,
    public authService: AuthService
  ) {
    super(modalCtrl);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.init();
  }

  async init() {
    this.targets = [
      {
        label: 'Mutuelle',
        value: 'mutuelle',
      },
      {
        label: 'Assuré',
        value: 'insured',
      },
    ];
    combineLatest([
      this.dataService.enterprises,
      this.documentService.getDocuments(
        'enterprise', // Mutuelle does not have library
        AppComponent.enterprise.value?.id
      ),
      this.populationService.insureds,
      this.authService.user,
    ]).subscribe({
      next: ([enterprises, documents, insureds, user]) => {
        this.user = user;
        // Set destination title
        this.destinationTitle =
          this.user.accessType === 'mutuelle'
            ? 'une entreprise'
            : 'un assuré ou ma mutuelle';

        // Set items for select controls
        this.enterprises = enterprises.map((e) => ({
          label: e.name,
          value: e.id,
        }));
        this.documents = documents.map((e) => ({
          label: e.title,
          value: e.uuid,
        }));
        this.insureds = insureds
          .filter((member) => this.isShareRecipientEligible(member))
          .map((e) => ({
            label: e.fullnameFormatted(),
            value: e.id,
          }));
        // For enterprise, we need to validate the target and insureds fields
        if (user.accessType === 'enterprise') {
          this.form.get('target').valueChanges.subscribe((value) => {
            this.selectedTarget = value;
            if (value === 'insured') {
              // Target is insured : validate insureds field
              this.form
                .get('insureds')
                .setValidators([Validators.required, Validators.minLength(1)]);
            } else {
              // Target is mutuelle : clear insureds field
              this.form.get('insureds').clearValidators();
              this.form.get('insureds').updateValueAndValidity();
            }
          });
        }
        // For mutuelle, we need to validate enterprises fields
        if (user.accessType === 'mutuelle') {
          this.form.get('target').setValue('enterprise');
          this.form
            .get('enterprises')
            .setValidators([Validators.required, Validators.minLength(1)]);
        }
      },
    });

    // setTimeout(() => {
    //   if (this.insured) {
    //     // this.insuredsSelectItems.push({label: this.insured.fullnameFormatted(), value: this.insured.id});
    //     this.selectedInsured = this.insured.id;
    //   }
    //   if (this.enterprisesSelection) {
    //     this.selectedEnterprises = this.enterprisesSelection.map((e) => e.id);
    //   }
    //   this.documentsSelection.forEach((document) => {
    //     if (this.documents.findIndex((e) => e.uuid === document.uuid) === -1) {
    //       this.documentsSelectItems.push({
    //         label: document.title,
    //         value: document.uuid,
    //       });
    //     }
    //   });
    //   this.selectedDocuments = this.documentsSelection.map((e) => e.uuid);
    // }, 150);
  }

  onAllInsuredsChecked(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.form.get('insureds').setValue(this.insureds.map((i) => i.value));
    } else {
      this.form.get('insureds').setValue([]);
    }
  }

  async createShare() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    await LoaderUtils.present();

    const shareData = {
      target: this.form.get('target').value,
      documents: this.form.get('documents').value,
      message: this.form.get('message').value,
      insuredIds: this.form.get('insureds').value || [],
      enterpriseIds: this.form.get('enterprises').value || [],
      files: this.form.get('files').value || [],
    };

    if (shareData.target === 'enterprise') {
      delete shareData.insuredIds;
    }
    if (shareData.target === 'insured') {
      delete shareData.enterpriseIds;
    }

    // Handle share response
    const handleShare = (res: any) => {
      LoaderUtils.dismiss();
      this.toastService.open({
        header: 'Envoi réussi',
        message: 'Votre envoi a été effectué avec succès',
        type: 'success',
      });
      this.close();
    };

    const handleShareError = (err: any) => {
      LoaderUtils.dismiss();
      this.toastService.open({
        header: 'Erreur',
        message: "Une erreur est survenue lors de l'envoi",
        type: 'error',
      });
    };

    // Send share to the right target
    if (this.user.accessType === 'mutuelle') {
      this.shareService
        .createShareByMutuelle(shareData)
        .then(handleShare)
        .catch(handleShareError);
    }
    if (this.user.accessType === 'enterprise') {
      this.shareService
        .createShareByEnterprise(shareData)
        .then(handleShare)
        .catch(handleShareError);
    }
  }

  removeFile(file: PickedFile) {
    const files = this.form.get('files').value;
    files.splice(files.indexOf(file), 1);
    this.form.get('files').setValue(files);
  }

  addFile($event: PickedFile[]) {
    const file = $event[0];
    const files = this.form.get('files').value;
    files.push(file);
    this.form.get('files').setValue(files);
  }

  private isShareRecipientEligible(member: Insured): boolean {
    if (environment.config?.shares?.recipientsActiveOnly !== true) {
      return true;
    }
    if (![0, 5].includes(member.status)) {
      return false;
    }
    const endDate = member.endDate
      ? moment(member.endDate, 'DD/MM/YYYY')
      : null;
    return !endDate || endDate.isSameOrAfter(moment(), 'day');
  }
}
